import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Laptop, Compass, TrendingUp } from "lucide-react";

interface ActivityProfile {
  profile: "sedentaire" | "nomade" | "terrain";
  distinct_active_days: number;
  total_events: number;
  avg_events_per_day: number;
  modules_count: number;
  typical_window_utc: string | null;
  days_covered: number;
  by_day: { day: string; events: number }[];
  top_modules: { module: string; count: number }[];
}

const PROFILE_META: Record<
  ActivityProfile["profile"],
  { label: string; color: string; bar: string; icon: typeof Laptop; desc: string }
> = {
  sedentaire: {
    label: "Sédentaire",
    color: "text-emerald-400",
    bar: "bg-emerald-500",
    icon: Laptop,
    desc: "Présent quotidiennement. Grafton utilise tout le dashboard pour vous servir.",
  },
  nomade: {
    label: "Nomade",
    color: "text-sky-400",
    bar: "bg-sky-500",
    icon: Compass,
    desc: "Présent quelques jours par semaine. Grafton privilégie des emails détaillés.",
  },
  terrain: {
    label: "Terrain",
    color: "text-amber-400",
    bar: "bg-amber-500",
    icon: Building2,
    desc: "Peu connecté. Grafton vous envoie des synthèses courtes et prioritaires.",
  },
};

function formatModule(m: string): string {
  if (!m) return "—";
  if (m.startsWith("/api/")) return m.replace(/^\/api\//, "api · ");
  return m;
}

export default function MyGraftonProfileCard() {
  const [data, setData] = useState<ActivityProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/activity/profile?days=7`;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card data-testid="grafton-profile-card-loading">
        <CardContent className="py-6 text-sm text-muted-foreground">Chargement de votre profil…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const meta = PROFILE_META[data.profile] ?? PROFILE_META.terrain;
  const Icon = meta.icon;
  const maxEvents = Math.max(1, ...data.by_day.map((b) => b.events));

  return (
    <Card data-testid="grafton-profile-card" className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Mon profil Grafton
          </CardTitle>
          <Badge variant="outline" className="gap-1.5" data-testid="grafton-profile-badge">
            <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
            <span className={meta.color}>{meta.label}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground" data-testid="grafton-profile-desc">
          {meta.desc}
        </p>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-muted-foreground">Jours actifs (7j)</div>
            <div className="text-xl font-semibold" data-testid="grafton-profile-days">
              {data.distinct_active_days} / 7
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Events / jour</div>
            <div className="text-xl font-semibold" data-testid="grafton-profile-avg">
              {data.avg_events_per_day}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Modules utilisés</div>
            <div className="text-xl font-semibold" data-testid="grafton-profile-modules-count">
              {data.modules_count}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Fenêtre</div>
            <div className="text-xl font-semibold" data-testid="grafton-profile-window">
              {data.typical_window_utc ?? "—"}
            </div>
          </div>
        </div>

        {/* Mini chart 7 days */}
        <div>
          <div className="text-xs text-muted-foreground mb-2">Activité des 7 derniers jours</div>
          <div className="flex items-end gap-1.5 h-20" data-testid="grafton-profile-chart">
            {data.by_day.map((b) => {
              const h = Math.round((b.events / maxEvents) * 100);
              const label = new Date(b.day).toLocaleDateString("fr-FR", { weekday: "short" });
              return (
                <div key={b.day} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-t ${b.events > 0 ? meta.bar : "bg-muted"}`}
                    style={{ height: `${Math.max(h, 4)}%`, minHeight: 4 }}
                    title={`${b.day} — ${b.events} events`}
                  />
                  <div className="text-[10px] text-muted-foreground">{label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 3 modules */}
        {data.top_modules.length > 0 && (
          <div>
            <div className="text-xs text-muted-foreground mb-2">Modules les plus utilisés</div>
            <div className="space-y-1.5" data-testid="grafton-profile-top-modules">
              {data.top_modules.slice(0, 3).map((m, i) => {
                const pct = Math.round((m.count / (data.top_modules[0]?.count || 1)) * 100);
                return (
                  <div key={m.module} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-xs text-muted-foreground">{i + 1}.</span>
                    <span className="flex-1 truncate font-mono text-xs">{formatModule(m.module)}</span>
                    <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full ${meta.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-10 text-right text-xs text-muted-foreground">{m.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
